import React from 'react';
import {
  ClipboardDocumentIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  UserIcon,
  CurrencyEuroIcon,
  LinkIcon,
  XMarkIcon,
  ChartBarIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowPathIcon,
  BellIcon,
  BellSlashIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { SidebarCard } from './SidebarCard';
import type { AffaireSettings, AffaireContact } from '../../types/tracker.types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAppStore } from '@/app/store';
import './CaseSidebar.styles.css';

/**
 * Dial a phone number through the embedded VoIP widget. Dispatches
 * a `pkv:dial` event with a mutable `handled` flag — the widget marks
 * it true synchronously when consumed (regardless of the SIP register
 * state). If nothing's listening (widget not mounted at all), prompt
 * the user to open + register the phone module instead of falling
 * back to a `tel:` link.
 */
function dialContact(rawNumber: string): void {
  const number = rawNumber.replace(/\s+/g, '').trim();
  if (!number) return;
  const detail = { number, handled: false };
  window.dispatchEvent(new CustomEvent('pkv:dial', { detail }));
  if (!detail.handled) {
    useAppStore.getState().addNotification({
      type: 'warning',
      title: 'Module téléphonique indisponible',
      message:
        "Ouvrez le panneau 📞 (en bas à droite) et attendez l'enregistrement avant de lancer l'appel.",
    });
  }
}

/**
 * Internal-call helper for intervenants. Slugifies the email local-part
 * the same way the VoIP control-plane auto-provisions sip_user
 * (services/control-plane/src/auth-bridge.ts → uniqueSipUser). Caveat:
 * if the agent's email collided on first-provision, their SIP user has
 * a numeric suffix that we can't reproduce client-side. Acceptable for
 * a small team — falls back to a not-found INVITE which the caller
 * sees as "Not Found".
 */
function emailToSipUser(email: string | null | undefined): string | null {
  if (!email) return null;
  const local = email.split('@')[0]?.toLowerCase() ?? '';
  const slug = local
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  return slug || null;
}

function dialIntervenant(email: string | null | undefined): void {
  const sipUser = emailToSipUser(email);
  if (!sipUser) {
    useAppStore.getState().addNotification({
      type: 'warning',
      title: 'Appel impossible',
      message: 'Pas de courriel renseigné pour cet intervenant.',
    });
    return;
  }
  dialContact(sipUser);
}

/**
 * Contact information (legacy single contact)
 */
interface ContactInfo {
  name: string;
  email?: string;
  phone?: string;
  type?: string;
}

/**
 * Payment status type for color-coded display
 */
type PaymentStatusType = 'no_products' | 'a_facturer' | 'a_facturer_partiel' | 'en_attente' | 'paye_partiel' | 'paye';

/**
 * Finance information for sidebar display
 */
interface FinanceInfo {
  totalHonorairesTvac?: number;
  expertHonorairesTvac?: number;
  paymentStatus?: PaymentStatusType;
  totalPaid?: number;
  totalInvoiced?: number;
  isDirectPayment?: boolean; // No products but payment matches affaire amount
  productCount?: number; // Number of product codes added to the affaire
}

/**
 * Props for CaseSidebar component
 */
interface CaseSidebarProps {
  /** Affaire ID */
  affaireId: number;

  /** Status label */
  status: string;

  /** Amount (TVAC) */
  amount?: number;

  /** Last activity date */
  lastActivityDate?: Date;

  /** Client contact info (legacy single contact) */
  client?: ContactInfo;

  /** All affaire contacts */
  contacts?: AffaireContact[];

  /** Property address */
  address?: {
    street?: string;
    number?: string;
    postcode?: string;
    city?: string;
  };

  /** Expert name */
  expert?: string;

  /** Affaire settings (responsables) */
  affaireSettings?: AffaireSettings | null;

  /** Key dates (IN, RV, Deadline, OUT) + EVAL 1/2/3 */
  dates?: {
    dateIn?: string;
    dateRv?: string;
    dateSortie?: string;
    encodeur?: string;
    encodeurUserId?: string;
    rvPrisPar?: string;
    rvPrisParUserId?: string;
    eval1?: string;
    eval2?: string;
    eval3?: string;
  };

  /** Finance information */
  finance?: FinanceInfo;

  /** Is sidebar open (mobile) */
  isOpen?: boolean;

  /** Callback to close sidebar (mobile) */
  onClose?: () => void;

  /** Callback for FileMaker link */
  onFileMaker?: () => void;

  /** Callback for billing/facturation */
  onBilling?: () => void;

  /** Callback to view responsable history */
  onViewHistory?: () => void;

  /** Callback to change responsable */
  onChangeResponsable?: (line: 'primary' | 'second' | 'third') => void;

  /** Can user change responsable */
  canChangeResponsable?: boolean;

  /** Callback to change status */
  onChangeStatus?: () => void;

  /** Whether the user is subscribed to this affaire */
  isSubscribed?: boolean;

  /** Callback to toggle subscription */
  onToggleSubscription?: () => void;

  /** Whether subscription is loading */
  subscriptionLoading?: boolean;

  /** Callback to open note composer with a pre-selected assignee */
  onOpenNote?: (userId: string) => void;
}

/**
 * Format currency for display
 * Returns object with value and isUnknown flag for styling
 */
const formatCurrency = (amount?: number): { value: string; isUnknown: boolean } => {
  if (amount === undefined || amount === null) {
    return { value: '—', isUnknown: true };
  }
  return {
    value: new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount),
    isUnknown: false,
  };
};

/**
 * Format user name
 */
const formatUserName = (user: any): string => {
  if (!user || !user.id) return 'Non assigné';
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Utilisateur inconnu';
};

/**
 * Get user initials
 */
const getUserInitials = (user: any): string => {
  if (!user || !user.id) return '?';
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (!name) return user.email?.[0]?.toUpperCase() || '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
};

/**
 * Get contact initials from prenom and nom_representant
 */
const getContactInitials = (prenom?: string, nom?: string): string => {
  const name = `${prenom || ''} ${nom || ''}`.trim();
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
};

/**
 * Copy text to clipboard
 */
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

/**
 * Add working days to a date (skips Saturday and Sunday)
 */
const addWorkingDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return result;
};

/**
 * Format a date string (ISO) to dd/MM/yyyy
 */
const formatDateShort = (dateStr?: string): string | null => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return format(date, 'dd/MM/yyyy', { locale: fr });
  } catch {
    return null;
  }
};

/**
 * CaseSidebar Component
 *
 * Right sidebar showing case details organized in cards:
 * - Résumé (status, amount, last activity)
 * - Dates clés (IN, RV, Deadline, OUT)
 * - Contacts (client info)
 * - Bien (property address)
 * - Intervenants (responsables, expert)
 * - Finances (invoice, payment status)
 * - Raccourcis (quick links)
 */
export const CaseSidebar: React.FC<CaseSidebarProps> = ({
  affaireId,
  status,
  amount,
  lastActivityDate,
  client,
  contacts,
  address,
  expert,
  affaireSettings,
  dates,
  finance,
  isOpen,
  onClose,
  onFileMaker,
  onBilling,
  onViewHistory,
  onChangeResponsable,
  canChangeResponsable,
  onChangeStatus,
  isSubscribed,
  onToggleSubscription,
  subscriptionLoading,
  onOpenNote,
}) => {
  // Build full address string
  const fullAddress = [
    address?.number,
    address?.street,
    address?.postcode,
    address?.city,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside className={`case-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Mobile close button */}
        <div className="sidebar-mobile-header">
          <span className="sidebar-mobile-title">Détails</span>
          <button
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="sidebar-content">
          {/* Résumé - Metrics Header */}
          <SidebarCard title="Résumé" icon={<ChartBarIcon className="w-4 h-4" />} className="accent-highlight">
            <div className="sidebar-resume">
              {/* 2-column grid: Status | Amount */}
              <div className="sidebar-resume-grid">
                {/* Statut */}
                <div className="sidebar-resume-cell">
                  <span className="sidebar-resume-label">Statut</span>
                  <span className="sidebar-status-badge">{status}</span>
                </div>

                {/* Montant */}
                <div className="sidebar-resume-cell right">
                  <span className="sidebar-resume-label">Montant</span>
                  {(() => {
                    const formatted = formatCurrency(amount);
                    return (
                      <span
                        className={`sidebar-resume-metric ${formatted.isUnknown ? 'empty' : ''}`}
                        title={formatted.isUnknown ? 'Non défini' : undefined}
                      >
                        {formatted.isUnknown ? 'Non défini' : formatted.value}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Dernière activité - Meta row */}
              {lastActivityDate && (
                <div className="sidebar-resume-meta">
                  <span className="sidebar-resume-meta-icon">
                    <ClockIcon className="w-3.5 h-3.5" />
                  </span>
                  <span className="sidebar-resume-meta-label">Dernière activité</span>
                  <span className="sidebar-resume-meta-value">
                    {format(lastActivityDate, "dd/MM 'à' HH:mm", { locale: fr })}
                  </span>
                </div>
              )}
            </div>
          </SidebarCard>

          {/* Dates clés - IN, RV, Deadline, OUT */}
          {dates && (dates.dateIn || dates.dateRv || dates.dateSortie) && (
            <SidebarCard title="Dates clés" icon={<CalendarDaysIcon className="w-4 h-4" />}>
              <div className="sidebar-dates">
                {/* IN - date_demande */}
                <div
                  className={`sidebar-date-row ${onOpenNote && dates.encodeurUserId ? 'sidebar-date-row--clickable' : ''}`}
                  onClick={() => {
                    if (onOpenNote && dates.encodeurUserId) onOpenNote(dates.encodeurUserId);
                  }}
                >
                  <span className="sidebar-date-label">IN</span>
                  <span className={`sidebar-date-value ${!dates.dateIn ? 'empty' : ''}`}>
                    {formatDateShort(dates.dateIn) || '—'}
                  </span>
                  {dates.encodeur && (
                    <span className="sidebar-date-person-chip">
                      <span className="sidebar-date-person-avatar">
                        {dates.encodeur.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                      </span>
                      <span className="sidebar-date-person-name">{dates.encodeur}</span>
                    </span>
                  )}
                </div>

                {/* RV - date_du_rv */}
                <div
                  className={`sidebar-date-row ${onOpenNote && dates.rvPrisParUserId ? 'sidebar-date-row--clickable' : ''}`}
                  onClick={() => {
                    if (onOpenNote && dates.rvPrisParUserId) onOpenNote(dates.rvPrisParUserId);
                  }}
                >
                  <span className="sidebar-date-label">RV</span>
                  <span className={`sidebar-date-value ${!dates.dateRv ? 'empty' : ''}`}>
                    {formatDateShort(dates.dateRv) || '—'}
                  </span>
                  {dates.rvPrisPar && (
                    <span className="sidebar-date-person-chip">
                      <span className="sidebar-date-person-avatar">
                        {dates.rvPrisPar.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                      </span>
                      <span className="sidebar-date-person-name">{dates.rvPrisPar}</span>
                    </span>
                  )}
                </div>

                {/* Deadline - RV + 5 working days */}
                <div className="sidebar-date-row">
                  <span className="sidebar-date-label">Deadline</span>
                  {(() => {
                    if (!dates.dateRv) {
                      return <span className="sidebar-date-value empty">—</span>;
                    }
                    const rvDate = new Date(dates.dateRv);
                    if (isNaN(rvDate.getTime())) {
                      return <span className="sidebar-date-value empty">—</span>;
                    }
                    const deadline = addWorkingDays(rvDate, 5);
                    const now = new Date();
                    const isOverdue = !dates.dateSortie && now > deadline;
                    const isMet = !!dates.dateSortie && new Date(dates.dateSortie) <= deadline;
                    return (
                      <>
                        <span className={`sidebar-date-value ${isOverdue ? 'overdue' : ''} ${isMet ? 'met' : ''}`}>
                          {format(deadline, 'dd/MM/yyyy', { locale: fr })}
                        </span>
                        {expert && (
                          <span className="sidebar-date-person-chip">
                            <span className="sidebar-date-person-avatar">
                              {expert.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                            </span>
                            <span className="sidebar-date-person-name">{expert}</span>
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* OUT - date_sortie */}
                <div className="sidebar-date-row">
                  <span className="sidebar-date-label">OUT</span>
                  <span className={`sidebar-date-value ${!dates.dateSortie ? 'empty' : ''}`}>
                    {formatDateShort(dates.dateSortie) || '—'}
                  </span>
                </div>

                {/* EVAL 1/2/3 - valorisation chain (intervenant style) */}
                {(dates.eval1 || dates.eval2 || dates.eval3) && (
                  <div className="sidebar-intervenants" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #f3f4f6' }}>
                    {dates.eval1 && (
                      <div className="sidebar-intervenant">
                        <span className="sidebar-intervenant-avatar">
                          {dates.eval1.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                        </span>
                        <div className="sidebar-intervenant-details">
                          <span className="sidebar-intervenant-role">EVAL 1</span>
                          <span className="sidebar-intervenant-name">{dates.eval1}</span>
                        </div>
                      </div>
                    )}
                    {dates.eval2 && (
                      <div className="sidebar-intervenant">
                        <span className="sidebar-intervenant-avatar">
                          {dates.eval2.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                        </span>
                        <div className="sidebar-intervenant-details">
                          <span className="sidebar-intervenant-role">EVAL 2</span>
                          <span className="sidebar-intervenant-name">{dates.eval2}</span>
                        </div>
                      </div>
                    )}
                    {dates.eval3 && (
                      <div className="sidebar-intervenant">
                        <span className="sidebar-intervenant-avatar">
                          {dates.eval3.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                        </span>
                        <div className="sidebar-intervenant-details">
                          <span className="sidebar-intervenant-role">EVAL 3</span>
                          <span className="sidebar-intervenant-name">{dates.eval3}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SidebarCard>
          )}

          {/* Contacts - exclude experts (shown in Intervenants) */}
          {(() => {
            const nonExpertContacts = contacts?.filter(
              (c) => !c.roles.some((role) => role.toUpperCase() === 'EXPERT')
            ) || [];
            return nonExpertContacts.length > 0 ? (
              <SidebarCard title={`Contacts (${nonExpertContacts.length})`} icon={<UserIcon className="w-4 h-4" />}>
                <div className="sidebar-contacts-list">
                  {nonExpertContacts.map((contact) => (
                    <div key={`${contact.k_id_contact}-${contact.nom_representant}-${contact.tel_mobile || ''}`} className="sidebar-contact-row">
                      <span className="sidebar-contact-avatar">
                        {getContactInitials(contact.prenom, contact.nom_representant)}
                      </span>
                      <div className="sidebar-contact-row-info">
                        <span className="sidebar-contact-row-name">
                          {contact.prenom} {contact.nom_representant}
                        </span>
                        {contact.roles.length > 0 && (
                          <span className="sidebar-contact-row-role">
                            {contact.roles.join(' • ')}
                          </span>
                        )}
                      </div>
                      <div className="sidebar-contact-row-actions">
                        {contact.email_1 && (
                          <button
                            className="sidebar-icon-btn"
                            onClick={() => copyToClipboard(contact.email_1!)}
                            title={contact.email_1}
                          >
                            <EnvelopeIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {contact.tel_mobile && (
                          <button
                            type="button"
                            className="sidebar-icon-btn"
                            onClick={() => dialContact(contact.tel_mobile!)}
                            title={`Appeler ${contact.tel_mobile}`}
                          >
                            <PhoneIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </SidebarCard>
          ) : client && (
            <SidebarCard title="Contacts" icon={<UserIcon className="w-4 h-4" />}>
              <div className="sidebar-contact">
                <div className="sidebar-contact-name">{client.name}</div>
                {client.type && (
                  <div className="sidebar-contact-type">{client.type}</div>
                )}
                <div className="sidebar-contact-actions">
                  {client.email && (
                    <button
                      className="sidebar-contact-btn"
                      onClick={() => copyToClipboard(client.email!)}
                      title={client.email}
                    >
                      <EnvelopeIcon className="w-4 h-4" />
                      <span className="sidebar-contact-btn-text">{client.email}</span>
                    </button>
                  )}
                  {client.phone && (
                    <button
                      type="button"
                      className="sidebar-contact-btn"
                      onClick={() => dialContact(client.phone!)}
                      title={`Appeler ${client.phone}`}
                    >
                      <PhoneIcon className="w-4 h-4" />
                      <span className="sidebar-contact-btn-text">{client.phone}</span>
                    </button>
                  )}
                </div>
              </div>
            </SidebarCard>
          )
        })()}

          {/* Bien (Property) */}
          {fullAddress && (
            <SidebarCard title="Bien" icon={<MapPinIcon className="w-4 h-4" />}>
              <div className="sidebar-property">
                <div className="sidebar-property-icon">
                  <MapPinIcon className="w-3.5 h-3.5" />
                </div>
                <div className="sidebar-property-content">
                  {address?.number && address?.street && (
                    <div className="sidebar-property-street">{`${address.number} ${address.street}`}</div>
                  )}
                  {(address?.postcode || address?.city) && (
                    <div className="sidebar-property-city">{`${address?.postcode || ''} ${address?.city || ''}`.trim()}</div>
                  )}
                </div>
                <div className="sidebar-property-actions">
                  <button
                    className="sidebar-icon-btn"
                    onClick={() => copyToClipboard(fullAddress)}
                    title="Copier l'adresse"
                  >
                    <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </SidebarCard>
          )}

          {/* Intervenants */}
          <SidebarCard
            title="Intervenants"
            icon={<UserIcon className="w-4 h-4" />}
            action={
              onViewHistory && (
                <button className="sidebar-card-action-btn" onClick={onViewHistory}>
                  <ArrowPathIcon className="w-3 h-3" />
                  Historique
                </button>
              )
            }
          >
            <div className="sidebar-intervenants">
              {/* Primary responsable */}
              {affaireSettings && (
                <div
                  className={`sidebar-intervenant ${onOpenNote && affaireSettings.primary_responsable?.id ? 'sidebar-intervenant--clickable' : ''}`}
                  onClick={() => {
                    if (onOpenNote && affaireSettings.primary_responsable?.id) {
                      onOpenNote(affaireSettings.primary_responsable.id);
                    }
                  }}
                >
                  <span className="sidebar-intervenant-avatar">
                    {getUserInitials(affaireSettings.primary_responsable)}
                  </span>
                  <div className="sidebar-intervenant-details">
                    <span className="sidebar-intervenant-role">Responsable</span>
                    <span className="sidebar-intervenant-name">
                      {formatUserName(affaireSettings.primary_responsable)}
                    </span>
                  </div>
                  <div className="sidebar-intervenant-actions">
                    {affaireSettings.primary_responsable?.email && (
                      <button
                        className="sidebar-icon-btn"
                        onClick={(e) => { e.stopPropagation(); dialIntervenant(affaireSettings.primary_responsable!.email); }}
                        title={`Appeler ${formatUserName(affaireSettings.primary_responsable)}`}
                      >
                        <PhoneIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canChangeResponsable && onChangeResponsable && (
                      <button
                        className="sidebar-icon-btn"
                        onClick={(e) => { e.stopPropagation(); onChangeResponsable('primary'); }}
                        title="Changer"
                      >
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Expert */}
              {expert && (
                <div className="sidebar-intervenant">
                  <span className="sidebar-intervenant-avatar">
                    {expert.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)}
                  </span>
                  <div className="sidebar-intervenant-details">
                    <span className="sidebar-intervenant-role">Expert</span>
                    <span className="sidebar-intervenant-name">{expert}</span>
                  </div>
                </div>
              )}

              {/* Second line (RV) */}
              {affaireSettings?.second_line_responsable?.id && (
                <div
                  className={`sidebar-intervenant ${onOpenNote ? 'sidebar-intervenant--clickable' : ''}`}
                  onClick={() => {
                    if (onOpenNote && affaireSettings.second_line_responsable?.id) {
                      onOpenNote(affaireSettings.second_line_responsable.id);
                    }
                  }}
                >
                  <span className="sidebar-intervenant-avatar">
                    {getUserInitials(affaireSettings.second_line_responsable)}
                  </span>
                  <div className="sidebar-intervenant-details">
                    <span className="sidebar-intervenant-role">RV (2ème ligne)</span>
                    <span className="sidebar-intervenant-name">
                      {formatUserName(affaireSettings.second_line_responsable)}
                    </span>
                  </div>
                  <div className="sidebar-intervenant-actions">
                    {affaireSettings.second_line_responsable?.email && (
                      <button
                        className="sidebar-icon-btn"
                        onClick={(e) => { e.stopPropagation(); dialIntervenant(affaireSettings.second_line_responsable!.email); }}
                        title={`Appeler ${formatUserName(affaireSettings.second_line_responsable)}`}
                      >
                        <PhoneIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canChangeResponsable && onChangeResponsable && (
                      <button
                        className="sidebar-icon-btn"
                        onClick={(e) => { e.stopPropagation(); onChangeResponsable('second'); }}
                        title="Changer"
                      >
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Third line (Support) */}
              {affaireSettings?.third_line_responsable?.id && (
                <div
                  className={`sidebar-intervenant ${onOpenNote ? 'sidebar-intervenant--clickable' : ''}`}
                  onClick={() => {
                    if (onOpenNote && affaireSettings.third_line_responsable?.id) {
                      onOpenNote(affaireSettings.third_line_responsable.id);
                    }
                  }}
                >
                  <span className="sidebar-intervenant-avatar">
                    {getUserInitials(affaireSettings.third_line_responsable)}
                  </span>
                  <div className="sidebar-intervenant-details">
                    <span className="sidebar-intervenant-role">Support (3ème ligne)</span>
                    <span className="sidebar-intervenant-name">
                      {formatUserName(affaireSettings.third_line_responsable)}
                    </span>
                  </div>
                  <div className="sidebar-intervenant-actions">
                    {affaireSettings.third_line_responsable?.email && (
                      <button
                        className="sidebar-icon-btn"
                        onClick={(e) => { e.stopPropagation(); dialIntervenant(affaireSettings.third_line_responsable!.email); }}
                        title={`Appeler ${formatUserName(affaireSettings.third_line_responsable)}`}
                      >
                        <PhoneIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canChangeResponsable && onChangeResponsable && (
                      <button
                        className="sidebar-icon-btn"
                        onClick={(e) => { e.stopPropagation(); onChangeResponsable('third'); }}
                        title="Changer"
                      >
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SidebarCard>

          {/* Finances - Color-coded payment status */}
          {finance && (
            <SidebarCard title="Finances" icon={<BanknotesIcon className="w-4 h-4" />}>
              <div className="sidebar-finances">
                {/* Product count - always show */}
                <div className="sidebar-finance-row">
                  <span className="sidebar-finance-label">Produits</span>
                  <span className="sidebar-finance-value">
                    {finance.productCount === 0 || finance.productCount === undefined ? (
                      <span className="sidebar-finance-empty">Aucun produit ajouté</span>
                    ) : (
                      <span>{finance.productCount} produit{finance.productCount > 1 ? 's' : ''}</span>
                    )}
                  </span>
                </div>
                {/* Direct payment indicator */}
                {finance.isDirectPayment && (
                  <div className="sidebar-finance-notice">
                    Paiement direct (hors facturation)
                  </div>
                )}
                {/* Honoraires total */}
                {finance.totalHonorairesTvac !== undefined && finance.paymentStatus !== 'no_products' && (
                  <div className="sidebar-finance-row">
                    <span className="sidebar-finance-label">Honoraires</span>
                    <span className="sidebar-finance-value">
                      {formatCurrency(finance.totalHonorairesTvac).value}
                    </span>
                  </div>
                )}
                {/* Expert honoraires (only show if > 0) */}
                {finance.expertHonorairesTvac !== undefined && finance.expertHonorairesTvac > 0 && finance.paymentStatus !== 'no_products' && (
                  <div className="sidebar-finance-row">
                    <span className="sidebar-finance-label">H. Expert</span>
                    <span className="sidebar-finance-value sidebar-finance-expert">
                      {formatCurrency(finance.expertHonorairesTvac).value}
                    </span>
                  </div>
                )}
                {/* Payment status with color-coded badge */}
                {finance.paymentStatus && finance.paymentStatus !== 'no_products' && (
                  <div className="sidebar-finance-row">
                    <span className="sidebar-finance-label">Paiement</span>
                    <span className="sidebar-finance-value">
                      {(() => {
                        const status = finance.paymentStatus;
                        const statusConfig: Record<PaymentStatusType, { label: string; className: string }> = {
                          'no_products': { label: '', className: '' },
                          'a_facturer': { label: 'À facturer', className: 'sidebar-payment-badge status-facturer' },
                          'a_facturer_partiel': { label: 'À facturer (partiel)', className: 'sidebar-payment-badge status-facturer' },
                          'en_attente': { label: 'En attente', className: 'sidebar-payment-badge status-attente' },
                          'paye_partiel': { label: 'Partiel', className: 'sidebar-payment-badge status-partiel' },
                          'paye': { label: 'Payé', className: 'sidebar-payment-badge status-paye' },
                        };
                        const config = statusConfig[status];

                        // For partial payment, show amounts
                        if (status === 'paye_partiel' && finance.totalPaid !== undefined && finance.totalInvoiced !== undefined) {
                          return (
                            <span className={config.className}>
                              {config.label} ({formatCurrency(finance.totalPaid).value} / {formatCurrency(finance.totalInvoiced).value})
                            </span>
                          );
                        }

                        return (
                          <span className={config.className}>
                            {config.label}
                          </span>
                        );
                      })()}
                    </span>
                  </div>
                )}
              </div>
            </SidebarCard>
          )}

          {/* Raccourcis */}
          <SidebarCard title="Raccourcis" icon={<LinkIcon className="w-4 h-4" />}>
            <div className="sidebar-shortcuts">
              {onToggleSubscription && (
                <button
                  className={`sidebar-shortcut-btn ${isSubscribed ? 'subscribed' : ''}`}
                  onClick={onToggleSubscription}
                  disabled={subscriptionLoading}
                >
                  {isSubscribed ? (
                    <BellSlashIcon className="w-4 h-4" />
                  ) : (
                    <BellIcon className="w-4 h-4" />
                  )}
                  <span>{isSubscribed ? 'Ne plus suivre' : 'Suivre'}</span>
                </button>
              )}
              {onFileMaker && (
                <button className="sidebar-shortcut-btn" onClick={onFileMaker}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="7" width="18" height="14" rx="2" ry="2" />
                    <rect x="8" y="7" width="8" height="5" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </svg>
                  <span>FileMaker</span>
                </button>
              )}
              {onBilling && (
                <button className="sidebar-shortcut-btn" onClick={onBilling}>
                  <CurrencyEuroIcon className="w-4 h-4" />
                  <span>Facturation</span>
                </button>
              )}
            </div>
          </SidebarCard>
        </div>
      </aside>
    </>
  );
};

export default CaseSidebar;
